import React, { useMemo, useCallback } from 'react';
import { Select, useTheme, colors } from '@iq/react-components';
import './SelectWidget.scss';

const SelectWidget = (properties) => {
  const {
    disabled,
    readonly,
    required,
    value,
    schema: {
      default: defaultValue,
      isMulti,
      isClearable,
      noOptionsMessage,
      extraText,
      extraTextTriggers = [],
      extraTextKey,
      placeholder: schemaPlaceholder,
      editable = true,
      groupedOptions,
    },
    options: { enumOptions = [] },
    onChange,
    ...otherProperties
  } = properties;
  const getLabel = useCallback(
    (value_) => enumOptions.find((o) => o.value === value_)?.label,
    [enumOptions]
  );
  const theme = useTheme();
  const theValue = useMemo(() => {
    if (isMulti) {
      let coercedValue = value;
      if (!Array.isArray(value)) {
        coercedValue = [value];
      }

      return (coercedValue || []).map((selectedValue) => ({
        label: getLabel(selectedValue) ?? String(selectedValue),
        value: selectedValue,
      }));
    }
    if (value || value === 0) {
      const label = getLabel(value);
      if (label !== undefined) {
        return { label, value };
      }
    }
    if (defaultValue) {
      const label = getLabel(defaultValue);
      return label === undefined ? null : { label, value: defaultValue };
    }
  }, [value, defaultValue, getLabel, isMulti]);

  const setValues = useCallback(
    (selected) => {
      if (isMulti) {
        onChange((selected || []).map((s) => s.value));
      } else {
        onChange(selected?.value || selected?.value === 0 ? selected.value : null);
      }
    },
    [onChange, isMulti]
  );

  const triggeredExtraTextKeys = useMemo(() => {
    let coercedValue = value || [];
    if (!Array.isArray(value)) {
      coercedValue = [value];
    }
    return [...new Set(coercedValue).intersection(new Set(extraTextTriggers))];
  }, [value, extraTextTriggers]);

  const getEmptyMessage = (text) => <div>{text}</div>;

  const overrideStyles = {
    container: (base) => ({
      ...base,
      ...(disabled || readonly ? { cursor: 'not-allowed' } : {}),
      lineHeight: 'normal',
    }),
    singleValue: (base) => ({ ...base, lineHeight: 'normal' }),
    control: (base) => ({
      ...base,
      ...(disabled || readonly
        ? {
            border: `1px solid ${theme === 'dark' ? colors.Grey70 : colors.Grey20}`,
            backgroundColor: theme === 'dark' ? colors.Grey90 : colors.Grey10,
            cursor: 'not-allowed',
          }
        : {}),
      lineHeight: 'normal',
    }),
  };

  const options =
    groupedOptions ||
    enumOptions.map((opt) => {
      const cleanedOpt = { ...opt };
      delete cleanedOpt.schema;
      return cleanedOpt;
    });

  return editable ? (
    <>
      {extraTextKey ? <div className="extra-text">{extraText[extraTextKey]}</div> : null}
      {triggeredExtraTextKeys.map((k, index) => {
        const key = `extra-text-${index}`;
        return (
          <div
            key={key}
            className="extra-text"
          >
            {extraText[k]}
          </div>
        );
      })}
      <Select
        {...otherProperties}
        key={`unique_select_key`}
        className="schema-select-widget"
        isMulti={isMulti}
        isClearable={isClearable}
        closeMenuOnSelect={!isMulti}
        isDisabled={disabled}
        readOnly={readonly}
        required={required}
        placeholder={schemaPlaceholder}
        menuPlacement="auto"
        value={theValue}
        onChange={setValues}
        options={options}
        noOptionsMessage={() => getEmptyMessage(noOptionsMessage || 'No options')}
        styles={overrideStyles}
      />
    </>
  ) : (
    <>
      {Array.isArray(theValue) ? (
        theValue.map((value_, index) => {
          const key = `${value_}-${index}`;
          return (
            <div
              key={key}
              className="view-widget-multi"
            >
              <label className="view-widget">{`${value_.label}`}</label>
            </div>
          );
        })
      ) : (
        <label className="view-widget">{theValue?.label ?? ''}</label>
      )}
    </>
  );
};

export default SelectWidget;
